import { queryOptions } from '@tanstack/react-query'
import type { TicketRow } from '../easeedesk/columns'
import { apiGet, apiPost } from './client'
import type { TicketChatMessage } from './easeedesk'

/**
 * Suportul prin chat - `EaseedeskChatSupportController`. Perechile vechi:
 *
 *   chatTicketsQuery         easeedesk/chatTicketsData        (tabelul de sus al ecranului)
 *   chatPanelQuery           easeedesk/quickChatSupport/{id}  (capul panoului si uneltele lui)
 *   fetchNewChatMessages     chat_conversation/getNewMessages/{id}  (la 10 secunde)
 *   chatPreviousQuery        chat_conversation/lastChatMessages/{id}  ("Details")
 *   chatUsersQuery           chat_conversation/getUsersList
 *   chatCommandsQuery        chat_conversation/getCmdCommandsList
 *   forceChatStatus          easeedesk/changeStatus/{id}      (Confirm for closing / Lost)
 *   inviteToChat             chat_conversation/inviteUserToChat
 *   changeChatTicket         chat_conversation/changeChatTicket
 *   suggestChatAction        chat_conversation/addNewActionSuggestion
 */

export interface ChatTicketsPage {
  /** `has_quick_chat_support` al omului: fara el, ecranul e lista obisnuita. */
  enabled: boolean
  draw: number
  recordsTotal: number
  recordsFiltered: number
  data: TicketRow[]
}

/** Toate discutiile mele, cele mai proaspete primele (portalul nu pagina tabelul). */
export const chatTicketsQuery = queryOptions({
  queryKey: ['easeedesk', 'chat', 'tickets'],
  queryFn: () => apiGet<ChatTicketsPage>('/easeedesk/chat-tickets-table?draw=1&start=0&length=200'),
})

export interface ChatPanel {
  ticket: number
  status: number
  /** 1 = activ: se poate scrie; altfel "This conversation is closed!". */
  status_type: number
  affected_user: string
  affected_device: { ID: number; device_name: string; owner_id: number } | null
  expert_requests: number
  chat_last_tickets: number
  predefined_messages: { id: number; message: string }[]
  /** Butonul "Change to <type_name>", cand setarile de chat ale firmei il dau. */
  change_ticket: { type_id: number; type_name: string; status_id: number } | null
  chat_settings: { received_message_color: string; sent_message_color: string }
  can_change_status: boolean
  categories: ValueName[]
  subcategories: ValueName[]
  operations: ValueName[]
}

/** O optiune de formular, ca in `TicketFormOptionsSource` (`value`/`name`). */
export interface ValueName {
  value: number
  name: string
}

export function chatPanelQuery(id: number, category = 0, subcategory = 0) {
  const p = new URLSearchParams()
  if (category > 0) p.set('category', String(category))
  if (subcategory > 0) p.set('subcategory', String(subcategory))
  const qs = p.toString()
  return queryOptions({
    queryKey: ['easeedesk', 'ticket', id, 'chat', 'panel', category, subcategory],
    queryFn: () => apiGet<ChatPanel>(`/easeedesk/tickets/${id}/chat/panel${qs === '' ? '' : '?' + qs}`),
  })
}

/** Un mesaj nou, in acelasi plic ca `TicketChatMessage`, plus de unde vine. */
export interface ChatNewMessage extends TicketChatMessage {
  /** 1 = de pe portalul de oaspete (`host-message` / `guest-message`). */
  source: number
}

export type ChatRecommendedAction =
  | { action_type: 'message'; action_id: number; id: number; message: string }
  | { action_type: 'command'; action_id: number; id: number; command_id: number; display_name: string; has_parameters: number; info: string }

export interface ChatNewMessages {
  ticket: number
  messages: ChatNewMessage[]
  recommended_actions: ChatRecommendedAction[]
}

export function fetchNewChatMessages(id: number): Promise<ChatNewMessages> {
  return apiGet<ChatNewMessages>(`/easeedesk/tickets/${id}/chat/new`)
}

export interface ChatPrevious {
  ticket: number
  previous: number
  messages: ChatNewMessage[]
}

export function chatPreviousQuery(id: number) {
  return queryOptions({
    queryKey: ['easeedesk', 'ticket', id, 'chat', 'previous'],
    queryFn: () => apiGet<ChatPrevious>(`/easeedesk/tickets/${id}/chat/previous`),
  })
}

export interface ChatUser {
  value: number
  name: string
  /** 1 = a lucrat in ultimele 15 minute (bulina verde). */
  status: number
}

export const chatUsersQuery = queryOptions({
  queryKey: ['easeedesk', 'chat', 'users'],
  queryFn: () => apiGet<ChatUser[]>('/easeedesk/chat/users'),
})

export interface ChatCommand {
  id: number
  display_name: string
  command: string
  has_parameters: number
  info: string
}

export const chatCommandsQuery = queryOptions({
  queryKey: ['easeedesk', 'chat', 'commands'],
  queryFn: () => apiGet<ChatCommand[]>('/easeedesk/chat/commands'),
})

export interface ChatStatusReply {
  status: string
  ticket: number
  ticketStatus: number
}

export function forceChatStatus(
  id: number,
  force: 'closing' | 'lost',
  closing: { ticket_category: string; ticket_subcategory: string; ticket_operation: string } = { ticket_category: '', ticket_subcategory: '', ticket_operation: '' },
): Promise<ChatStatusReply> {
  return apiPost<ChatStatusReply>(`/easeedesk/tickets/${id}/chat/status`, { force_status: force, ...closing })
}

export function inviteToChat(id: number, userId: number): Promise<{ status: string }> {
  return apiPost<{ status: string }>(`/easeedesk/tickets/${id}/chat/invite`, { user_id: userId })
}

export interface ChatTicketChanged {
  status: string
  ticket: number
  newTicket: number
  chatTicketMoved: boolean
  chatTicketMessage: string
}

export function changeChatTicket(id: number, newTicketType: number, ticketStatus: number): Promise<ChatTicketChanged> {
  return apiPost<ChatTicketChanged>(`/easeedesk/tickets/${id}/chat/change-ticket`, {
    new_ticket_type: newTicketType,
    ticket_status: ticketStatus,
  })
}

export function suggestChatAction(id: number, keywords: string, messages: string[], commands: number[]): Promise<{ status: string; action: number }> {
  return apiPost<{ status: string; action: number }>(`/easeedesk/tickets/${id}/chat/suggest-action`, {
    keywords,
    messages,
    commands,
  })
}
